import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { sendLeadToSheet } from "@/lib/leads.functions";
import { EpiProvider } from "@/lib/epi";

import { ArrowRight, ArrowUp, Check, X, Clock, ShieldCheck, CheckCircle2, Lock, Pencil, Package } from "lucide-react";
import { Footer } from "@/components/laneguide/Footer";
import { AmountInput } from "@/components/laneguide/AmountInput";
import { LoanWarning } from "@/components/laneguide/LoanWarning";
import { LenderCard } from "@/components/laneguide/CompareList";
import { LenderLogo } from "@/components/laneguide/LenderLogo";
import { StarRating } from "@/components/laneguide/StarRating";
import { LENDERS, type Lender } from "@/lib/lenders";
import {
  matchPartners,
  eligibleRules,
  ucBucketMin,
  KFM_NOTICE,
  OVER_MAX_NOTICE,
  displayChance,
} from "@/lib/partnerMatching";

import logo from "@/assets/logo-header.webp";

export const Route = createFileRoute("/lp/samling-lan")({
  head: () => ({
    meta: [
      { title: "Samling-lån – se hur mycket du kan spara | Svensk Låneguide" },
      {
        name: "description",
        content:
          "Har du mer än 30 000 kr i skuld? Se på under 2 minuter hur mycket du kan spara på att samla dina lån och krediter i ett samling-lån.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Samling-lån – se hur mycket du kan spara" },
      { property: "og:description", content: "Svara på några snabba frågor och få personliga låneförslag." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: LandingPage,
});

const fmt = (n: number) => new Intl.NumberFormat("sv-SE").format(n);

/** Alla banker vi jobbar med, inkl. förmedlarnas anslutna banker. */
const TOTAL_BANKS = 52;

/** Hur många förslag vi visar i slutet. */
const MAX_RESULTS = 5;

type Answers = {
  amount: number;
  debtTypes: string[];
  goal: string;
  kfm: boolean | null;
  remarks: boolean | null;
  ucCount: string | null;
  income: number;
};

/** Förmedlare som skickar din ansökan vidare till flera banker. */
const BROKERS = ["lendo", "sambla", "enklare", "advisa", "axo", "loansse", "klaralan"];

/** Visningsvärden för förmedlare (spannet hos deras anslutna banker). */
const BROKER_TERMS: Record<string, { rate: string; term: string }> = {
  lendo: { rate: "från 4,50 %", term: "1 – 20 år" },
  sambla: { rate: "från 4,50 %", term: "1 – 20 år" },
  enklare: { rate: "från 4,50 %", term: "1 – 20 år" },
  advisa: { rate: "från 4,50 %", term: "1 – 20 år" },
  axo: { rate: "från 4,50 %", term: "1 – 20 år" },
  loansse: { rate: "från 4,50 %", term: "1 – 20 år" },
};

function matchInput(a: Answers) {
  return {
    amount: a.amount,
    remarks: a.remarks === true,
    kfm: a.kfm === true,
    ucMin: ucBucketMin(a.ucCount),
    income: a.income > 0 ? a.income : null,
  };
}

/** Alla partners som klarar uteslutningsfiltreringen (steg 1). */
function eligible(a: Answers): Lender[] {
  return eligibleRules(matchInput(a))
    .map((r) => LENDERS[r.id])
    .filter((l): l is Lender => Boolean(l));
}


function reasonFor(l: Lender, a: Answers) {
  const bits: string[] = [];
  bits.push(`klarar hela ditt belopp på ${fmt(a.amount)} kr`);
  if (l.categories.includes("Samla lån")) bits.push("är specialiserade på att samla lån och krediter");
  if (a.remarks || a.kfm) bits.push("accepterar ansökningar även med betalningsanmärkning");
  if (!l.usesUc) bits.push("tar ingen UC-upplysning");
  else if (a.ucCount === "4-5" || a.ucCount === "6+")
    bits.push("gör bara en kreditupplysning trots att du redan har flera");
  if (l.directPayout) bits.push("betalar ut pengarna direkt");
  const t = BROKER_TERMS[l.id];
  const rate = t ? `Ränta ${t.rate}` : `Räntespann ${l.rate}`;
  const term = t ? t.term : l.term;
  return `${l.name} ${bits.join(", ")}. ${rate} och ${term} återbetalningstid – bäst helhet utifrån dina svar.`;
}

const DEBT_TYPES = ["Kreditkort", "Privatlån", "Inkasso", "Annat"];
const GOALS = [
  { id: "lagre", label: "Betala mindre varje månad" },
  { id: "enkel", label: "En enkel månadsbetalning" },
  { id: "snabbt", label: "En tydlig väg till 0 kr skuld" },
  { id: "osaker", label: "Jag är inte säker" },
];
const UC_OPTIONS = [
  { id: "0", label: "Ingen alls" },
  { id: "1", label: "1 stycken" },
  { id: "2-3", label: "2–3 stycken" },
  { id: "4-5", label: "4–5 stycken" },
  { id: "6+", label: "6 eller fler" },
];

const TOTAL_STEPS = 8;


function LandingPage() {
  const [step, setStep] = useState(0);
  const [a, setA] = useState<Answers>({
    amount: 250000,
    debtTypes: [],
    goal: "",
    kfm: null,
    remarks: null,
    ucCount: null,
    income: 0,
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [openId, setOpenId] = useState<string | null>(null);
  

  const count = useMemo(() => eligible(a).length, [a]);
  const match = useMemo(() => matchPartners(matchInput(a)), [a]);
  const results = match.lenders;

  const pct = ((step + 1) / TOTAL_STEPS) * 100;
  const set = (p: Partial<Answers>) => setA((prev) => ({ ...prev, ...p }));
  const slider = ((a.amount - 5000) / (800000 - 5000)) * 100;

  return (
    <EpiProvider value="lp-samling-lan">
    <div className="min-h-screen bg-background">
      <div className="border-b border-border/60 bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <img src={logo} alt="Svensk Låneguide" width={290} height={100} className="h-9 w-auto sm:h-10" />
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Lock className="h-4 w-4 text-primary" /> Säker ansökan
          </div>
        </div>
      </div>

      <main className={`mx-auto px-4 pb-16 pt-8 sm:px-6 ${done ? "max-w-7xl" : "max-w-2xl"}`}>
        {!done ? (
          <>
            <div className="text-sm font-bold text-primary">Mer pengar i fickan</div>
            <h1 className="mt-2 font-display text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-[2.6rem]">
              Mer än <span className="underline-brand">30 000 kr</span> i skuld?
              <br />
              Se hur mycket du kan spara med ett samling-lån
            </h1>
            <ul className="mt-5 space-y-2 text-[15px] text-foreground/85">
              {[
                "Många sparar 40 % eller mer på sina månadskostnader",
                "Samla krediter, lån och räkningar i ett samling-lån",
                "Gratis och helt utan förpliktelser",
                `Tillgång till ${TOTAL_BANKS} långivare via licensierade kreditförmedlare`,
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 h-2 w-full overflow-hidden rounded-md bg-muted">
              <div className="h-full rounded-md bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>

            <div className="mt-5 rounded-md border border-border bg-card p-6 shadow-elevated sm:p-8">
              <div className="mb-4 rounded-md bg-primary-soft px-3 py-2 text-sm font-semibold text-foreground">
                <div className="flex items-center justify-between gap-3">
                  <span>Långivare som matchar dig just nu</span>
                  <span className="font-display text-xl font-extrabold text-primary">{count}</span>
                </div>
                <p className="mt-0.5 text-xs font-medium text-foreground/70">
                  Vi visar dina {Math.min(results.length, MAX_RESULTS)} bäst matchande förslag i slutet.
                </p>
              </div>


              {step === 0 && (
                <div>
                  <h2 className="font-display text-xl font-extrabold text-foreground">Hur mycket skuld har du?</h2>
                  <p className="text-sm text-muted-foreground">Ansök på under 2 minuter</p>
                  <div className="mt-5 text-center font-display text-4xl font-extrabold text-primary">
                    {fmt(a.amount)} kr
                  </div>
                  <input
                    type="range"
                    min={5000}
                    max={800000}
                    step={5000}
                    value={a.amount}
                    onChange={(e) => set({ amount: Number(e.target.value) })}
                    style={{ ["--val" as string]: `${slider}%` } as React.CSSProperties}
                    className="range-slider mt-4 w-full"
                  />
                  <div className="mt-1 flex justify-between text-[11px] font-medium text-muted-foreground">
                    <span>5 000 kr</span>
                    <span>800 000 kr</span>
                  </div>
                  <label className="mt-4 block text-sm font-semibold text-foreground">Eller ange belopp:</label>
                  <div className="mt-1 flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2">
                    <AmountInput
                      value={a.amount}
                      onChange={(n) => set({ amount: n })}
                      max={800000}
                      className="w-full bg-transparent text-[15px] outline-none"
                    />
                    <span className="text-sm font-semibold text-muted-foreground">kr</span>
                  </div>
                  <NextButton onClick={() => setStep(1)} />
                  <Badges />
                </div>
              )}

              {step === 1 && (
                <Step onBack={() => setStep(0)}>
                  <h2 className="font-display text-xl font-extrabold text-foreground">Vilken typ av skuld har du?</h2>
                  <p className="text-sm text-muted-foreground">Välj alla som stämmer</p>
                  <div className="mt-4 space-y-2">
                    {DEBT_TYPES.map((t) => {
                      const on = a.debtTypes.includes(t);
                      return (
                        <Option
                          key={t}
                          label={t}
                          active={on}
                          onClick={() =>
                            set({ debtTypes: on ? a.debtTypes.filter((x) => x !== t) : [...a.debtTypes, t] })
                          }
                        />
                      );
                    })}
                  </div>
                  <NextButton onClick={() => setStep(2)} disabled={!a.debtTypes.length} />
                </Step>
              )}

              {step === 2 && (
                <Step onBack={() => setStep(1)}>
                  <h2 className="font-display text-xl font-extrabold text-foreground">
                    Vad skulle hjälpa dig mest just nu?
                  </h2>
                  <div className="mt-4 space-y-2">
                    {GOALS.map((g) => (
                      <Option
                        key={g.id}
                        label={g.label}
                        active={a.goal === g.id}
                        onClick={() => {
                          set({ goal: g.id });
                          setStep(3);
                        }}
                      />
                    ))}
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step onBack={() => setStep(2)}>
                  <YesNo
                    title="Har du obetalda skulder hos Kronofogden?"
                    sub="Skulder hos Kronofogden är inte ett hinder för att ansöka."
                    onPick={(v) => {
                      set({ kfm: v });
                      setStep(4);
                    }}
                  />
                </Step>
              )}

              {step === 4 && (
                <Step onBack={() => setStep(3)}>
                  <YesNo
                    title="Har du betalningsanmärkningar?"
                    sub="En betalningsanmärkning är inte ett hinder för att ansöka."
                    onPick={(v) => {
                      set({ remarks: v });
                      setStep(5);
                    }}
                  />
                </Step>
              )}

              {step === 5 && (
                <Step onBack={() => setStep(4)}>
                  <h2 className="font-display text-xl font-extrabold text-foreground">
                    Vad har du för inkomst före skatt per månad?
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Inkomsten avgör vilka långivare som kan bevilja ditt lån.
                  </p>
                  <div className="mt-5 text-center font-display text-4xl font-extrabold text-primary">
                    {fmt(a.income)} kr
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={80000}
                    step={1000}
                    value={a.income}
                    onChange={(e) => set({ income: Number(e.target.value) })}
                    style={{ ["--val" as string]: `${(a.income / 80000) * 100}%` } as React.CSSProperties}
                    className="range-slider mt-4 w-full"
                  />
                  <div className="mt-1 flex justify-between text-[11px] font-medium text-muted-foreground">
                    <span>0 kr</span>
                    <span>80 000 kr</span>
                  </div>
                  <NextButton onClick={() => setStep(6)} />
                  <button
                    type="button"
                    onClick={() => {
                      set({ income: 0 });
                      setStep(6);
                    }}
                    className="mt-3 w-full rounded-md border border-border bg-card py-3 text-sm font-semibold text-foreground hover:bg-muted"
                  >
                    Hoppa över den här frågan
                  </button>
                </Step>
              )}

              {step === 6 && (
                <Step onBack={() => setStep(5)}>
                  <h2 className="font-display text-xl font-extrabold text-foreground">
                    Hur många kreditupplysningar har du de senaste 12 månaderna?
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Frivilligt – hjälper oss att matcha dig med rätt långivare.
                  </p>
                  <div className="mt-4 space-y-2">
                    {UC_OPTIONS.map((o) => (
                      <Option
                        key={o.id}
                        label={o.label}
                        active={a.ucCount === o.id}
                        onClick={() => {
                          set({ ucCount: o.id });
                          setStep(7);
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(7)}
                    className="mt-5 w-full rounded-md border border-border bg-card py-3 text-sm font-semibold text-foreground hover:bg-muted"
                  >
                    Hoppa över den här frågan
                  </button>
                </Step>
              )}

              {step === 7 && (
                <Step onBack={() => setStep(6)}>
                  <h2 className="font-display text-xl font-extrabold leading-snug text-foreground">
                    Fyll i dina uppgifter för att se hur mycket du kan spara varje månad på{" "}
                    <span className="text-primary">{fmt(a.amount)} kr</span>
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">Vi håller alltid dina uppgifter säkra.</p>
                  <div className="mt-5 space-y-4">
                    <Field label="Förnamn" value={name} onChange={setName} placeholder="Anna" />
                    <Field label="E-post" value={email} onChange={setEmail} placeholder="anna@example.se" type="email" />
                    <Field label="Mobilnummer" value={phone} onChange={setPhone} placeholder="070-123 45 67" type="tel" />
                  </div>
                  <p className="mt-5 rounded-md bg-primary-soft px-4 py-3 text-center text-sm font-semibold text-foreground">
                    Du kan få{" "}
                    <span className="font-display text-lg font-extrabold text-primary">{results.length}</span>{" "}
                    låneförslag baserat på dina svar
                  </p>
                  <button
                    onClick={async () => {
                      if (saving) return;
                      setSaving(true);
                      setSaveError(null);
                      const row = {
                        source: "lp/samling-lan",
                        name: name.trim(),
                        email: email.trim().toLowerCase(),
                        phone: phone.trim(),
                        amount: a.amount,
                        income: a.income,
                        goal: a.goal || null,
                        debt_types: a.debtTypes,
                        kfm: a.kfm,
                        remarks: a.remarks,
                        uc_count: a.ucCount,
                        matched_lenders: results.map((l) => l.name),
                      };
                      void sendLeadToSheet({
                        data: {
                          source: row.source,
                          name: row.name,
                          email: row.email,
                          phone: row.phone,
                          amount: row.amount,
                          income: row.income,
                          goal: row.goal,
                          debtTypes: row.debt_types,
                          kfm: row.kfm,
                          remarks: row.remarks,
                          ucCount: row.uc_count,
                          matchedLenders: row.matched_lenders,
                        },
                      }).catch(() => undefined);
                      let ok = false;
                      for (let i = 0; i < 2 && !ok; i++) {
                        const { error } = await supabase.from("lead_submissions").insert(row);
                        if (!error) ok = true;
                        else if (i === 1) setSaveError(error.message);
                      }

                      setSaving(false);
                      if (ok) setDone(true);
                    }}
                    disabled={!name || !email || !phone || saving}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-cta py-4 text-base font-bold text-cta-foreground shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--cta)_60%,transparent)] transition-all hover:brightness-105 disabled:opacity-50"
                  >
                    {saving ? "Skickar…" : "Se mina låneförslag"} <ArrowRight className="h-5 w-5" />
                  </button>
                  {saveError && (
                    <p className="mt-3 rounded-md bg-destructive/10 px-4 py-2 text-center text-sm font-semibold text-destructive">
                      Något gick fel – försök igen.
                    </p>
                  )}
                  <Badges />
                </Step>
              )}

            </div>
          </>
        ) : (
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {name ? `${name}, dina` : "Dina"} <span className="underline-brand">låneförslag</span>
            </h1>
            <p className="mt-2 text-sm text-foreground/70">
              {results.length} förslag matchar ditt samling-lån på {fmt(a.amount)} kr och dina svar.
            </p>

            {match.kind === "kfm" && (
              <p className="mt-4 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground/85">
                {KFM_NOTICE}
              </p>
            )}
            {match.kind === "over-max" && (
              <p className="mt-4 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground/85">
                {OVER_MAX_NOTICE}
              </p>
            )}


            {results[0] && (
              <section className="mt-6 rounded-md border border-primary/40 bg-card p-5 shadow-elevated ring-1 ring-primary/10">
                <div className="mb-3 inline-flex rounded-md bg-primary px-2.5 py-1 text-[11px] font-black uppercase tracking-wider text-primary-foreground">
                  Vårt toppval för dig
                </div>
                <p className="text-[15px] leading-relaxed text-foreground/85">{reasonFor(results[0], a)}</p>
              </section>
            )}

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              {results.map((l, idx) => (
                <LenderCard
                  key={l.id}
                  lender={l}
                  popularFallback={idx === 0}
                  open={openId === l.id}
                  onToggle={() => setOpenId(openId === l.id ? null : l.id)}
                  chance={displayChance(idx, a.remarks === true, a.kfm === true, l, a.amount)}
                />
              ))}
            </div>

            <LoanWarning className="mt-8" />

            <button
              onClick={() => {
                setDone(false);
                setStep(0);
              }}
              className="mt-6 flex items-center gap-2 text-sm font-semibold text-foreground/70 hover:text-foreground"
            >
              <ArrowUp className="h-4 w-4" /> Ändra mina svar
            </button>
          </div>
        )}
      </main>

      {!done && (
        <>
          <PartnerSection />
          <HowItWorks />
          <StickyCta />
        </>
      )}

      <Footer />
    </div>
    </EpiProvider>
  );
}

function Step({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        ← Tillbaka
      </button>
      {children}
    </div>
  );
}

function Option({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-md border p-4 text-left text-[15px] font-medium transition-all ${
        active ? "border-primary bg-primary-soft text-foreground" : "border-border bg-card hover:border-primary/40"
      }`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded border ${
          active ? "border-primary bg-primary text-primary-foreground" : "border-input"
        }`}
      >
        {active && <Check className="h-3.5 w-3.5" />}
      </span>
      {label}
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-foreground">{label} *</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
      />
    </div>
  );
}

function NextButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-cta py-4 text-base font-bold text-cta-foreground shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--cta)_60%,transparent)] transition-all hover:brightness-105 disabled:opacity-50"
    >
      Gå vidare <ArrowRight className="h-5 w-5" />
    </button>
  );
}

function YesNo({ title, sub, onPick }: { title: string; sub: string; onPick: (v: boolean) => void }) {
  return (
    <div>
      <h2 className="font-display text-xl font-extrabold text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
      <div className="mt-5 flex justify-center gap-4">
        <button
          onClick={() => onPick(true)}
          className="flex h-24 w-28 flex-col items-center justify-center gap-2 rounded-md border border-border bg-card font-semibold hover:border-primary"
        >
          <Check className="h-6 w-6 text-primary" /> Ja
        </button>
        <button
          onClick={() => onPick(false)}
          className="flex h-24 w-28 flex-col items-center justify-center gap-2 rounded-md border border-border bg-card font-semibold hover:border-primary"
        >
          <X className="h-6 w-6 text-primary" /> Nej
        </button>
      </div>
    </div>
  );
}

function Badges() {
  return (
    <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-3 rounded-md border border-border/70 bg-muted/40 px-3 py-3 text-[13px] font-medium text-foreground/80 sm:grid-cols-3">
      <span className="flex min-w-0 items-center justify-center gap-1.5">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">Gratis ansökan</span>
      </span>
      <span className="flex min-w-0 items-center justify-center gap-1.5">
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">100 % säker</span>
      </span>
      <span className="col-span-2 flex min-w-0 items-center justify-center gap-1.5 sm:col-span-1">
        <Clock className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">Tar 2 minuter</span>
      </span>
    </div>
  );
}

const PARTNERS = ["sambla", "axo", "enklare", "lendo"] as const;

function PartnerSection() {
  return (
    <section className="bg-primary-soft/60 py-12">
      <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Vi arbetar bara med de bästa
        </h2>
        <p className="mt-2 text-[15px] text-foreground/75">
          Våra samarbetspartners är bland de högst betygsatta på Trustpilot
        </p>
        <div className="mt-8 grid grid-cols-2 items-end gap-8 sm:grid-cols-4">
          {PARTNERS.map((id) => {
            const l = LENDERS[id];
            if (!l) return null;
            return (
              <div key={id} className="flex flex-col items-center gap-2">
                <div className="flex h-12 items-center">
                  <LenderLogo lender={l} size={120} bare className="!h-auto w-full" />
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="underline">Omdömen {fmt(l.reviews)}</span>
                  <StarRating rating={l.rating} />
                  <span className="font-semibold text-foreground">{l.rating.toString().replace(".", ",")}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Pencil, title: "1. Gör en ansökan", text: "Fyll i din ansökan digitalt. Det tar runt 2 minuter." },
  { icon: Package, title: "2. Vi kopplar ihop dig med en partner", text: "Vi förmedlar din intresseanmälan till en av våra licensierade samarbetspartners." },
  { icon: CheckCircle2, title: "3. Kolla om du kan spara pengar", text: "Efter att du ansökt via en av våra licensierade samarbetspartners kan du se om du har möjlighet att spara pengar på dina gamla lån." },
];

function HowItWorks() {
  return (
    <section className="bg-muted/40 py-14">
      <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
        <div className="text-sm font-bold text-primary">Hur fungerar Svensk Låneguide?</div>
        <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Vi hjälper dig till rätt lånepartner
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-foreground/75">
          Svensk Låneguide samarbetar med flera licensierade samarbetspartners på den svenska lånemarknaden och hjälper
          dig vidare till en av dem. Det är vår samarbetspartner som tar emot din ansökan och antingen jämför
          erbjudanden från långivare eller bedömer om de själva kan erbjuda finansiering.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="rounded-md border border-border bg-card p-6 shadow-card">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border-2 border-dashed border-primary/50 text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-base font-extrabold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/75">{s.text}</p>
              </div>
            );
          })}
        </div>

        <h2 className="mt-12 font-display text-2xl font-extrabold text-foreground sm:text-3xl">
          Ansök på under 2 minuter!
        </h2>
        <button
          onClick={scrollToForm}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-cta px-7 py-4 text-base font-bold text-cta-foreground shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--cta)_60%,transparent)] transition-all hover:brightness-105"
        >
          Klicka här och se hur mycket du kan spara
        </button>
      </div>
    </section>
  );
}

function scrollToForm() {
  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
}

function StickyCta() {
  return (
    <div className="pointer-events-none sticky bottom-4 z-40 flex justify-center px-4">
      <button
        onClick={scrollToForm}
        className="pointer-events-auto inline-flex items-center gap-2 rounded-md bg-cta px-6 py-3.5 text-sm font-bold text-cta-foreground shadow-elevated transition-all hover:brightness-105 sm:text-base"
      >
        <ArrowUp className="h-4 w-4" /> Klicka här och se hur mycket du kan spara
      </button>
    </div>
  );
}
